"use client";

import "../../types/mui-palette";
import { EuiProvider } from "@elastic/eui";
import { ReactNode } from "react";
import {
  ThemeProvider as MuiThemeProvider,
  THEME_ID,
  createTheme,
} from "@mui/material/styles";
import "@elastic/eui/dist/eui_theme_light.css";
import { Global, css } from "@emotion/react";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import { blueGrey } from "@mui/material/colors";
import { blue, indigo } from "@mui/material/colors";

declare module "@mui/material/styles" {
  interface Palette {
    blue: typeof blue;
    indigo: typeof indigo;
  }
  interface PaletteOptions {
    blue?: typeof blue;
    indigo?: typeof indigo;
  }
}

const materialTheme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: `Inter, "system-ui", Helvetica, Arial, sans-serif`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          fontFamily: `Inter, "system-ui", Helvetica, Arial, sans-serif`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#212B35",
          color: "#FFFFFF",
          fontSize: "0.875rem",
          padding: "8px 12px",
        },
      },
    },
  },
  typography: {
    fontFamily: `Inter, "system-ui", Helvetica, Arial, sans-serif`,
    h4: {
      fontSize: "20px",
      fontWeight: 600,
    },
    h5: {
      fontSize: "16px",
      fontWeight: 600,
    },
  },
  palette: {
    blueGrey,
    indigo,
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.60)",
    },
    secondary: {
      main: "#646A77",
    },
    primary: {
      main: "#0063F3",
      light: "#1D79FF",
      dark: "#264DB1",
      contrastText: "#FFFFFF",
    },
  },
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  return (
    <EuiProvider colorMode="LIGHT">
      <Global
        styles={css`
          html {
            background-color: white !important;
          }
          body {
            font-family: Inter, "system-ui", Helvetica, Arial, sans-serif;
          }
          .MuiSwitch-root input:disabled {
            opacity: 0;
          }
          * {
            /* Hide scrollbar for Firefox */
            scrollbar-width: none;
            scrollbar-color: transparent transparent;
            /* Hide scrollbar for IE, Edge */
            -ms-overflow-style: none;
          }
          *::-webkit-scrollbar {
            width: 0px;
            height: 0px;
            background: transparent;
            display: none;
          }
          *::-webkit-scrollbar-thumb {
            background: transparent;
            display: none;
          }
          *::-webkit-scrollbar-track {
            background: transparent;
            display: none;
          }
        `}
      />
      <MuiThemeProvider theme={{ [THEME_ID]: materialTheme }}>
        {children}
      </MuiThemeProvider>
    </EuiProvider>
  );
}
